import styles from "./Branches.module.scss";
import commonStyles from "../Common.module.scss";
import tabStyles from "../Tab.module.scss";
import iconStyles from "../../components/Icon.module.scss";
import buttonStyles from "../../components/Button.module.scss";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

interface BranchesData {
  location: string;
  address: string;
  otherPrograms: string;
  time: string;
  link: string;
}

const BRANCHES_DATA: Record<string, BranchesData> = {
  Berlin: {
    location: "Berlin",
    address:
      "Kaiser-Friedrich-Gedächtniskirche, Händelallee 20<br />10557, Berlin",
    otherPrograms:
      "Bible study<br />Choir<br />Organic Small Group<br />Prayer fellowship",
    time: "9:30 AM, 3:00 PM",
    link: "https://irec-berlin.org",
  },
  Hamburg: {
    location: "Hamburg",
    address: "Berner Heerweg 60<br />22159, Hamburg",
    otherPrograms:
      "Bible study<br />Choir<br />Organic Small Group<br />Prayer fellowship",
    time: "4:00 PM",
    link: "https://www.grii-hamburg.org/",
  },
  Munich: {
    location: "Munich",
    address: "Schumacherring 24<br />81737, Munich",
    otherPrograms:
      "Bible study<br />Choir<br />Organic Small Group<br />Prayer fellowship",
    time: "4:30 PM",
    link: "https://grii-munich.org/",
  },
  Swiss: {
    location: "Swiss",
    address: "Kirchstrasse 208<br>3084, Wabern",
    otherPrograms:
      "Bible study<br />Choir<br />Organic Small Group<br />Prayer fellowship",
    time: "4:00 PM",
    link: "https://grii-bern.org/en/",
  },
};

const Branches = () => {
  const [activeTab, setActiveTab] = useState<string>(
    Object.keys(BRANCHES_DATA)[0],
  );
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  const branch = BRANCHES_DATA[activeTab];

  useEffect(() => {
    // Animate content when tab changes
    setIsAnimating(true);
    const timeout = setTimeout(() => {
      setIsAnimating(false);
    }, 750);

    return () => clearTimeout(timeout);
  }, [activeTab]);

  return (
    <>
      <Helmet>
        <title>
          Branches | International Reformed Evangelical Church in Stockholm
        </title>
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta
          name="description"
          content="Find IREC branches in Berlin, Hamburg, and Munich. Discover worship times, service locations, and fellowship programs across Europe."
        />
        <meta
          name="keywords"
          content="international church, international church in stockholm, stockholm church, IREC branches, church branches europe, reformed evangelical branches, IREC Berlin, IREC Hamburg, IREC Munich, international church europe, PRII branches, reformed evangelical churches, GRII churches"
        />
        <meta
          itemProp="description"
          content="Find IREC branches in Berlin, Hamburg, and Munich. Discover worship times, service locations, and fellowship programs across Europe."
        />
        <meta
          name="og:title"
          property="og:title"
          content="Branches | International Reformed Evangelical Church in Stockholm"
        />
        <meta
          name="og:description"
          property="og:description"
          content="Find IREC branches in Berlin, Hamburg, and Munich. Discover worship times, service locations, and fellowship programs across Europe."
        />
        <meta name="og:type" property="og:type" content="website" />
        <meta
          name="og:url"
          property="og:url"
          content="https://irec-stockholm.org/branches"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Branches | International Reformed Evangelical Church in Stockholm"
        />
        <meta
          name="twitter:description"
          content="Find IREC branches in Berlin, Hamburg, and Munich. Discover worship times, service locations, and fellowship programs across Europe."
        />
        <meta
          name="og:image"
          property="og:image"
          content="https://irec-stockholm.org/carousel-01.png"
        />
        <meta
          name="og:image:alt"
          property="og:image:alt"
          content="IREC Branches in Europe - Berlin, Hamburg, Munich, and Swiss locations for worship and fellowship"
        />
        <meta name="og:image:width" property="og:image:width" content="1200" />
        <meta name="og:image:height" property="og:image:height" content="630" />
        <meta
          name="twitter:image"
          content="https://irec-stockholm.org/carousel-01.png"
        />
        <link rel="canonical" href="https://irec-stockholm.org/branches" />
      </Helmet>
      <div className={commonStyles.page}>
        <section id="branches" className={styles.branches}>
          <div className={styles.title}>
            <h3>Somewhere else in Europe?</h3>
            <p>We have branches in Germany and Switzerland</p>
          </div>
          <div className={tabStyles["tab-group"]}>
            {Object.keys(BRANCHES_DATA).map((name) => (
              <div
                key={name}
                className={`${tabStyles.tab} ${
                  activeTab === name ? tabStyles.active : ""
                }`}
                onClick={() => setActiveTab(name)}
              >
                {name}
              </div>
            ))}
          </div>

          <div
            className={styles.wrapper}
            style={{
              opacity: isAnimating ? 0 : 1,
              marginTop: isAnimating ? "20px" : "0",
              transition: isAnimating
                ? "none"
                : "opacity 0.5s ease, margin-top 0.5s ease",
            }}
          >
            <div className={styles.desc}>
              <div id="name" className={styles.name}>
                <div>
                  International Reformed
                  <br />
                  Evangelical Church
                </div>
                <h3 id="branch-location" className={styles["branch-location"]}>
                  in {branch.location}
                </h3>
              </div>
              <div className={styles.info}>
                <h6
                  id="branch-address"
                  className={styles["branch-address"]}
                  dangerouslySetInnerHTML={{ __html: branch.address }}
                ></h6>

                <a
                  href={branch.link}
                  className={`${buttonStyles.button}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Visit website
                  <i
                    className={`${iconStyles.icon} ${styles.icon} ${iconStyles.launch}`}
                  ></i>
                </a>
              </div>
            </div>
            <div className={styles.program}>
              <div id="service" className={styles.service}>
                <h4>Sunday service</h4>
                <div id="time" className={styles.time}>
                  {branch.time}
                </div>
              </div>
              <h6
                id="other-programs"
                className={styles["other-programs"]}
                dangerouslySetInnerHTML={{ __html: branch.otherPrograms }}
              ></h6>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Branches;
